/*
 * Desktop vertical collection filters: collapse / expand.
 *
 * - The X in the "Filters" header folds the filter options away (vertical,
 *   eased) and then the product grid extends left to reclaim the column.
 * - The X sits at the right edge of the filter column (titlebar width is set
 *   to the measured filter-column width).
 * - State is kept in memory per section and re-applied (without animation)
 *   after the collection section re-renders on sort / grid-density changes.
 * - Listeners are attached once at the document level so they survive the
 *   theme's section morphing. Honors prefers-reduced-motion.
 *
 * Classes this toggles:
 *   .facets-block-wrapper--vertical  -> .rr-collapsed (vertical fold), .rr-hidden (removed from grid)
 *   .collection-wrapper              -> .rr-filters-collapsed (--facets-vertical-col-width: 2)
 */
(function () {
  if (window.__rrFacetsCollapseInit) return;
  window.__rrFacetsCollapseInit = true;

  var FOLD_MS = 520;
  var SLIDE_MS = 540;
  var EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

  // sectionId -> { collapsed: bool, width: number }
  var state = Object.create(null);

  function reducedMotion() {
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (e) {
      return false;
    }
  }

  function isDesktop() {
    try {
      return window.matchMedia("(min-width: 750px)").matches;
    } catch (e) {
      return true;
    }
  }

  function ctx(btn) {
    var sectionId = btn.getAttribute("data-section-id");
    var panel = document.getElementById(btn.getAttribute("aria-controls"));
    var wrapper = btn.closest(".collection-wrapper");
    var titlebar = btn.closest(".facets--filters-titlebar");
    var grid = wrapper ? wrapper.querySelector(".main-collection-grid") : null;
    return { sectionId: sectionId, btn: btn, panel: panel, wrapper: wrapper, titlebar: titlebar, grid: grid };
  }

  // Record the filter-column width so the X can sit at its right edge, and keep
  // it set even while collapsed (when the panel itself has no width).
  function measure(c) {
    if (!c.panel || !c.titlebar) return;
    var s = state[c.sectionId] || (state[c.sectionId] = { collapsed: false, width: 0 });
    if (!s.collapsed && isDesktop()) {
      var w = c.panel.getBoundingClientRect().width;
      if (w > 0) s.width = w;
    }
    if (s.width > 0) {
      c.titlebar.style.setProperty("--rr-facets-col-width", s.width + "px");
    }
  }

  function slideGrid(grid, fromLeft) {
    if (!grid || reducedMotion()) return;
    var dx = fromLeft - grid.getBoundingClientRect().left;
    if (Math.abs(dx) < 1) return;
    // Clip horizontally while the grid is briefly offset so it cannot spawn a
    // horizontal scrollbar during the slide.
    var wrapper = grid.closest(".collection-wrapper");
    if (wrapper) wrapper.classList.add("rr-filters-animating");
    grid.style.transition = "none";
    grid.style.transform = "translateX(" + dx + "px)";
    grid.getBoundingClientRect(); // force reflow
    requestAnimationFrame(function () {
      grid.style.transition = "transform " + SLIDE_MS + "ms " + EASE;
      grid.style.transform = "translateX(0)";
    });
    window.setTimeout(function () {
      grid.style.transition = "";
      grid.style.transform = "";
      if (wrapper) wrapper.classList.remove("rr-filters-animating");
    }, SLIDE_MS + 80);
  }

  function setExpandedAttr(btn, collapsed) {
    btn.setAttribute("aria-expanded", collapsed ? "false" : "true");
  }

  // Apply the end state with no animation (initial load / after re-render).
  function applyInstant(c, collapsed) {
    if (!c.panel) return;
    setExpandedAttr(c.btn, collapsed);
    c.panel.classList.add("rr-no-anim");
    c.panel.classList.toggle("rr-collapsed", collapsed);
    c.panel.classList.toggle("rr-hidden", collapsed);
    if (c.wrapper) c.wrapper.classList.toggle("rr-filters-collapsed", collapsed);
    // Restore transitions next frame.
    requestAnimationFrame(function () {
      c.panel.classList.remove("rr-no-anim");
    });
    measure(c);
  }

  function collapse(c) {
    setExpandedAttr(c.btn, true);
    measure(c); // capture width while still expanded
    // 1) Fold the filter options vertically.
    c.panel.classList.add("rr-collapsed");
    // 2) After the fold, remove the column and let products extend left.
    var finish = function () {
      // Bail out if the user re-expanded before the fold finished.
      if (!state[c.sectionId] || !state[c.sectionId].collapsed) return;
      var fromLeft = c.grid ? c.grid.getBoundingClientRect().left : 0;
      c.panel.classList.add("rr-hidden");
      if (c.wrapper) c.wrapper.classList.add("rr-filters-collapsed");
      slideGrid(c.grid, fromLeft);
    };
    if (reducedMotion()) {
      finish();
    } else {
      window.setTimeout(finish, FOLD_MS);
    }
  }

  function expand(c) {
    setExpandedAttr(c.btn, false);
    // 1) Give the column back; products return to their narrower track.
    var fromLeft = c.grid ? c.grid.getBoundingClientRect().left : 0;
    c.panel.classList.remove("rr-hidden");
    if (c.wrapper) c.wrapper.classList.remove("rr-filters-collapsed");
    slideGrid(c.grid, fromLeft);
    // 2) Unfold the filter options vertically.
    requestAnimationFrame(function () {
      c.panel.classList.remove("rr-collapsed");
      measure(c);
    });
  }

  document.addEventListener("click", function (e) {
    var btn = e.target.closest && e.target.closest(".facets-collapse-toggle");
    if (!btn) return;
    e.preventDefault();
    var c = ctx(btn);
    if (!c.panel) return;
    var s = state[c.sectionId] || (state[c.sectionId] = { collapsed: false, width: 0 });
    s.collapsed = !s.collapsed;
    if (s.collapsed) collapse(c);
    else expand(c);
  });

  function syncAll() {
    var toggles = document.querySelectorAll(".facets-collapse-toggle");
    for (var i = 0; i < toggles.length; i++) {
      var c = ctx(toggles[i]);
      var s = state[c.sectionId];
      if (s && s.collapsed) {
        applyInstant(c, true);
      } else {
        measure(c);
      }
    }
  }

  // Re-apply persisted (in-memory) state after the section re-renders.
  var obs = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (
          (n.matches && n.matches(".facets-collapse-toggle")) ||
          (n.querySelector && n.querySelector(".facets-collapse-toggle"))
        ) {
          syncAll();
          return;
        }
      }
    }
  });
  obs.observe(document.body, { childList: true, subtree: true });

  var reMeasure = function () {
    var toggles = document.querySelectorAll(".facets-collapse-toggle");
    for (var i = 0; i < toggles.length; i++) measure(ctx(toggles[i]));
  };
  window.addEventListener("resize", reMeasure);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", syncAll);
  } else {
    syncAll();
  }
})();
